import { useEffect } from 'react';
import { AppShell, AppShellHeader, AppShellMain, Center, Container, Group, Loader } from '@mantine/core';
import { Outlet, createFileRoute, redirect, useLocation, useNavigate } from '@tanstack/react-router';
import { DEV_SWITCHER_OFFSET } from '@/components/dev-user-switcher';
import { useAuth } from '@/contexts/auth';
import { OrgProvider } from '@/contexts/org';
import { SoftphoneProvider } from '@/contexts/softphone';
import { getToken } from '@/lib/token';
import { BrandMark, Header } from './-components/header';

const noPaddingRoutes = ['/flows/', '/agent'];

const headerStyles = { top: DEV_SWITCHER_OFFSET };
const mainMinHeight = `calc(100dvh - ${DEV_SWITCHER_OFFSET})`;

export const Route = createFileRoute('/_authed')({
  beforeLoad: () => {
    if (!getToken()) throw redirect({ to: '/sign-in' });
  },
  component: AuthedLayout,
});

function LoadingShell() {
  return (
    <AppShell header={{ height: 60 }} styles={{ header: headerStyles, main: { minHeight: mainMinHeight } }}>
      <AppShellHeader
        className="cd-header-glass"
        style={{ backgroundColor: 'var(--cd-header-bg)', borderBottom: '1px solid var(--cd-header-border)' }}
      >
        <Container fluid h="100%" px="lg">
          <Group h="100%" wrap="nowrap">
            <BrandMark />
          </Group>
        </Container>
      </AppShellHeader>
      <AppShellMain>
        <Center h={`calc(100dvh - 60px - ${DEV_SWITCHER_OFFSET})`}>
          <Loader size="lg" />
        </Center>
      </AppShellMain>
    </AppShell>
  );
}

function AuthedLayout() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });

  useEffect(() => {
    if (!isLoading && !user) void navigate({ to: '/sign-in' });
  }, [isLoading, user, navigate]);

  if (isLoading || !user) return <LoadingShell />;

  return (
    <OrgProvider>
      <SoftphoneProvider>
        <AppShell
          header={{ height: 60 }}
          padding={noPaddingRoutes.some((route) => pathname.startsWith(route)) ? 0 : 'md'}
          styles={{
            header: headerStyles,
            main: { backgroundColor: 'var(--cd-bg-subtle)', minHeight: mainMinHeight },
          }}
        >
          <Header pathname={pathname} />
          <AppShellMain>
            <Outlet />
          </AppShellMain>
        </AppShell>
      </SoftphoneProvider>
    </OrgProvider>
  );
}
